import React, { ReactNode, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useQuery } from 'react-query';
import { Session } from '@ory/client';
import {
  getSession,
  getValidToken,
  isAccessTokenValid,
  sessionDisplayName,
  startLogin,
} from '../../auth/auth';
import { IS_MOCK_AUTH_MODE } from '../../auth/mock';
import { usePublicApiClient } from '../../hooks/useApiClient';
import useOrganizationStore from '../../store/organizationStore';
import useUserStore from '../../store/user';
import LoadingScreen from '../LoadingScreen';
import LoginFailedScreen from './LoginFailedScreen';
import ProfileSelectorScreen from './ProfileSelectorScreen';
import SplashMessage from './SplashMessage';

const messages = defineMessages({
  tenantsFailed: {
    id: 'require_login.tenants_failed',
    defaultMessage:
      'The organization list could not be loaded. Reload the page to try again.',
  },
  tenantUnknown: {
    id: 'require_login.tenant_unknown',
    defaultMessage:
      'Your organization is not available in this application. Contact support at support@allbinary.se.',
  },
  claimsFailed: {
    id: 'require_login.claims_failed',
    defaultMessage:
      'Your login could not be renewed. Reload the page to try again.',
  },
});

const RequireLogin: React.FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const publicClient = usePublicApiClient();
  const { setOrgs, lookup } = useOrganizationStore();
  const userProfileId = useUserStore((s) => s.userProfileId);
  const accessToken = useUserStore((s) => s.accessToken);
  const uid = useUserStore((s) => s.uid);
  const tenantCode = useUserStore((s) => s.tenantCode);
  const tenant = useUserStore((s) => s.tenant);
  const setName = useUserStore((s) => s.setName);
  const setTenant = useUserStore((s) => s.setTenant);
  const [loginFailed, setLoginFailed] = useState(false);

  const sessionQuery = useQuery<Session | null, Error>(
    ['allauth', 'session'],
    getSession,
    {
      enabled: !IS_MOCK_AUTH_MODE,
      staleTime: Infinity,
      // getSession only throws on transient failures (network, Kratos 5xx) —
      // "no session" is a null result, so retrying here is always safe.
      retry: 2,
      onError: () => {
        // Without a persisted profile the session is required to proceed;
        // with one, the app renders from persisted state and the failure
        // only costs the display name.
        if (!useUserStore.getState().userProfileId) {
          setLoginFailed(true);
        }
      },
    },
  );
  const session = sessionQuery.data;

  const tenantsQuery = useQuery(
    ['tenants', 'list'],
    () => publicClient.tenants.list(),
    {
      enabled: !IS_MOCK_AUTH_MODE,
      staleTime: Infinity,
      onSuccess: setOrgs,
      onError: () => undefined,
    },
  );

  // A persisted profile without identity claims (uid) means the token was
  // dropped before a reload (e.g. after an API 401) — the claims come from
  // the token, so mint eagerly before mounting anything that reads them.
  // Mint failures route through the mint-failure matrix (login redirect /
  // profile selector).
  const needsClaims = !IS_MOCK_AUTH_MODE && !!userProfileId && !uid;
  const claimsQuery = useQuery(['allauth', 'bootMint'], getValidToken, {
    enabled: needsClaims,
    retry: 2,
    onError: () => undefined,
  });

  // The token carries no display name; the Kratos identity traits do.
  useEffect(() => {
    if (session) {
      setName(sessionDisplayName(session));
    }
  }, [session, setName]);

  // Map the token's AllAuth tenant code to the internal `region-*` id that
  // tenant_features, GTFS_URLS and map helpers are keyed by. Mock mode is
  // primed directly by StubAllAuthProvider.
  const mappedTenant = (tenantCode && lookup[tenantCode]?.id) || '';
  useEffect(() => {
    if (IS_MOCK_AUTH_MODE) {
      return;
    }
    setTenant(mappedTenant);
  }, [mappedTenant, setTenant]);

  // No Kratos session and no still-valid token: neither profile selection nor
  // token renewal can succeed, so go (back) to the Kratos login flow.
  const needsLogin =
    !IS_MOCK_AUTH_MODE &&
    session === null &&
    (!userProfileId || !isAccessTokenValid(accessToken));

  useEffect(() => {
    if (needsLogin) {
      startLogin().catch(() => setLoginFailed(true));
    }
  }, [needsLogin]);

  if (IS_MOCK_AUTH_MODE) {
    return <>{children}</>;
  }

  if (loginFailed) {
    // Retry the whole boot flow; logging out here would destroy a possibly
    // healthy Kratos session over a transient failure.
    return (
      <LoginFailedScreen
        onConfirm={() => window.location.replace(window.location.origin)}
      />
    );
  }

  if (needsLogin) {
    return <LoadingScreen />;
  }

  if (!userProfileId) {
    if (!session) {
      return <LoadingScreen />;
    }
    return <ProfileSelectorScreen />;
  }

  if (needsClaims) {
    if (claimsQuery.isError) {
      return (
        <SplashMessage
          message={intl.formatMessage(messages.claimsFailed)}
          onRetry={() => window.location.replace(window.location.origin)}
        />
      );
    }
    return <LoadingScreen />;
  }

  // Gate on the tenant mapping: without it getTenantFeatures() silently
  // falls back to defaults and the GTFS client stays null. Also wait for the
  // mapping effect to commit, so children never mount with a stale tenant
  // (the index route resolves dashboardEnabled at mount).
  if (tenantsQuery.isError) {
    return (
      <SplashMessage
        message={intl.formatMessage(messages.tenantsFailed)}
        onRetry={() => window.location.replace(window.location.origin)}
      />
    );
  }
  if (!tenantsQuery.isSuccess || tenant !== mappedTenant) {
    return <LoadingScreen />;
  }
  // A tenant code the API doesn't know is a provisioning error — surface it
  // instead of rendering with silently-default tenant features.
  if (tenantCode && !lookup[tenantCode]) {
    return (
      <SplashMessage
        message={intl.formatMessage(messages.tenantUnknown)}
        onRetry={() => window.location.replace(window.location.origin)}
      />
    );
  }

  return <>{children}</>;
};

export default RequireLogin;
